"use client"

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useInitialCoins } from "@/hooks/coins/useInitialCoins"
import { TabsContent } from "@radix-ui/react-tabs"
import SupportTiers from "./SupportTiers"
import Trade from "./Trade"
import { CoinData } from "@/types/coins"
import { useUSDCoinQuote } from "@/hooks/coins/useUSDCoinQuote"
import { Skeleton } from "@/components/ui/skeleton"
import { useAccount } from "wagmi"
import { useSupporterTiers } from "@/hooks/coins/useSupporterTiers"

type Props = {
  coinData: CoinData
  ethPrice: number
  coinId: string
}

export default function SupportTabs({ coinData, ethPrice, coinId }: Props) {
  const { poolAddress, contractAddress } = coinData
  const initialCoins = useInitialCoins(contractAddress)
  const quote = useUSDCoinQuote(poolAddress, ethPrice)
  const account = useAccount()

  const hideTrade =
    !account.address ||
    (initialCoins !== undefined && parseInt(initialCoins) === 0) ||
    initialCoins === undefined
  const showSkeleton =
    !!account.address && (!quote || initialCoins === undefined)
  const coinsPerUSD = quote ? Number(quote.amountOut / BigInt(1e18)) : 0

  const supporterTiers = useSupporterTiers(coinId, coinsPerUSD)

  if (showSkeleton) {
    return (
      <div className="h-[550px] flex flex-col gap-5">
        <Skeleton className="h-[40px] rounded-lg mt-5" />
        <Skeleton className="h-[20px] rounded-sm" />
        <Skeleton className="h-[98px] rounded-md" />
        <Skeleton className="h-[98px] rounded-md" />
        <Skeleton className="h-[98px] rounded-md" />
        <Skeleton className="h-[40px] rounded-lg" />
        <Skeleton className="h-[20px] rounded-sm" />
      </div>
    )
  }

  if (hideTrade) {
    return (
      <SupportTiers
        coinsPerUSD={coinsPerUSD}
        quote={quote}
        ethPrice={ethPrice}
        poolAddress={poolAddress}
        tier1Supporters={supporterTiers.supportersTier1USD}
        tier5Supporters={supporterTiers.supportersTier5USD}
        tier10Supporters={supporterTiers.supportersTier10USD}
      />
    )
  }

  return (
    <Tabs defaultValue="support" className="w-full sm:mt-6 mt-4">
      <TabsList className="sm:w-full">
        <TabsTrigger value="support" className="sm:w-1/2">
          Support
        </TabsTrigger>
        <TabsTrigger value="trade" className="sm:w-1/2">
          Trade
        </TabsTrigger>
      </TabsList>

      <TabsContent value="support">
        <SupportTiers
          coinsPerUSD={coinsPerUSD}
          quote={quote}
          ethPrice={ethPrice}
          poolAddress={poolAddress}
          tier1Supporters={supporterTiers.supportersTier1USD}
          tier5Supporters={supporterTiers.supportersTier5USD}
          tier10Supporters={supporterTiers.supportersTier10USD}
        />
      </TabsContent>
      <TabsContent value="trade">
        <Trade
          initialCoins={initialCoins}
          coinsPerUSD={coinsPerUSD}
          ethPrice={ethPrice}
          poolAddress={poolAddress}
        />
      </TabsContent>
    </Tabs>
  )
}
